import axios, { AxiosInstance } from "axios";

import type {
  TBunker,
  TUser,
  TClient,
  TClientUser,
  TClientFleet,
  TBunkerPrice,
  TRequestForFirm,
  TPortNote,
} from "@/types";

class ApiClient {
  private client: AxiosInstance;

  constructor(baseURL: string) {
    function generateContextId(): string {
      return crypto.randomUUID(); // modern browsers
    }

    this.client = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add a request interceptor to set a new X-Content-Id for every request
    this.client.interceptors.request.use((config) => {
      config.headers = config.headers || {};
      // Always attach a fresh request id
      config.headers["X-Content-Id"] = generateContextId();
      // Always attach the latest auth token from localStorage
      const token = localStorage.getItem("access_token");
      if (token) {
        (config.headers as any)["Authorization"] = token;
      } else {
        // Ensure we don't send a stale/empty Authorization header
        if ((config.headers as any)["Authorization"]) {
          delete (config.headers as any)["Authorization"];
        }
      }
      return config;
    });
  }

  // Bunker endpoints
  async createBunker(bunker: TBunker) {
    return this.client.post("/bunker", bunker);
  }

  async getBunkers(bunkerId?: string) {
    return this.client.get("/bunker", {
      params: { id: bunkerId },
    });
  }

  async getVesselStatic(imo: string) {
    return this.client.get(`/vesselStatic/${imo}`);
  }

  async getVesselsByName(name?: string) {
    const response = await this.client.get("/vesselStatic", {
      params: { name },
    });
    return response.data;
  }

  // Client endpoints
  async upsertClient(client: TClient) {
    return this.client.post("/client", client);
  }

  async createClientUser(clientUser: TClientUser) {
    return this.client.post("/clientUser", clientUser);
  }

  async updateClientUser(clientUser: TClientUser) {
    return this.client.put("/clientUser", clientUser);
  }

  async getClients(clientId?: string) {
    return this.client.get("/client", { params: { id: clientId } });
  }

  async getClientUsers(email?: string, clientDomain?: string) {
    return this.client.get("/clientUser", { params: { email, clientDomain } });
  }

  async deleteClientUser(email: string, clientDomain: string) {
    return this.client.delete("/clientUser", {
      params: { email, clientDomain },
    });
  }

  async deleteClient(clientId: string) {
    return this.client.delete("/client", {
      params: { id: clientId },
    });
  }

  // ClientFleet endpoints
  async createClientFleet(clientFleet: TClientFleet) {
    return this.client.post("/clientFleet", clientFleet);
  }

  async getClientFleets(clientDomain?: string) {
    return this.client.get("/clientFleet", { params: { clientDomain } });
  }

  async getClientFleet(clientFleetId: string) {
    return this.client.get(`/clientFleet/${clientFleetId}`);
  }

  async deleteClientFleet(clientFleetId: string) {
    return this.client.delete(`/clientFleet/${clientFleetId}`);
  }

  async addVesselToClientFleet(clientFleetId: string, imo: string) {
    return this.client.post(`/clientFleet/${clientFleetId}/vessels`, { imo });
  }

  async removeVesselFromClientFleet(clientFleetId: string, imo: string) {
    return this.client.delete(`/clientFleet/${clientFleetId}/vessels/${imo}`);
  }

  async createBunkerPrice(bunkerPrice: TBunkerPrice) {
    return this.client.post("/bunkerPrice", bunkerPrice);
  }

  async getBunkerPrices(bunkerId?: string) {
    return await this.client.get(`/bunkerPrice`, { params: { bunkerId } });
  }

  async getBunkerPricesForAccountManager(
    accountManagerEmail: string,
    opts?: { page?: number; pageSize?: number }
  ) {
    return await this.client.get(`/bunkerPrice`, {
      params: {
        accountManagerEmail,
        page: opts?.page,
        pageSize: opts?.pageSize,
      },
    });
  }

  async deleteBunkerPrice(id: string) {
    return await this.client.delete(`/bunkerPrice`, { params: { id } });
  }

  async getClientFleetVessels(clientFleetId: string) {
    return this.client.get(`/clientFleet/${clientFleetId}/vessels`);
  }

  async getRoutesWithPorts(
    startLat: number,
    startLng: number,
    endLat: number,
    endLng: number,
    distanceNm: number
  ) {
    const response = await this.client.get("/bunker/route", {
      params: { startLat, startLng, endLat, endLng, distanceNm },
    });

    const data = response.data;

    const weatherMap = new Map<string, any[]>(Object.entries(data.weather));

    return { ...data, weather: weatherMap }; // Convert object to Map for easier handling
  }

  async getVesselPosition(imo: string) {
    const response = await this.client.get(`/vesselPosition/${imo}`);
    return response.data;
  }

  // Port endpoints
  async getPorts(search?: string) {
    return this.client.get("/port", { params: { search } });
  }

  async getPortCallings(imo: string) {
    return this.client.get(`/portCalling/${imo}`);
  }

  async addPortCallings(imo: string, portCallings: any[]) {
    return this.client.post(`/portCalling/${imo}`, portCallings);
  }

  async addRequestForFirm(requestForFirm: TRequestForFirm) {
    return this.client.post("/requestForFirm", requestForFirm);
  }

  async getRequestForFirms(
    clientDomain: string,
    opts?: { page?: number; pageSize?: number }
  ) {
    const params = new URLSearchParams({ clientDomain });
    if (opts?.page) params.append("page", String(opts.page));
    if (opts?.pageSize) params.append("pageSize", String(opts.pageSize));
    return this.client.get(`/requestForFirm?${params.toString()}`);
  }

  async getVesselRiskScore(imo?: string) {
    return this.client.get(`/vesselRiskScore/${imo}`);
  }

  async addAccountManager(user: TUser) {
    return this.client.post("/accountManager", user);
  }

  async updateAccountManager(user: TUser) {
    return this.client.put("/accountManager", user);
  }

  async getAccountManager(email?: string) {
    return this.client.get("/accountManager", {
      params: { email },
    });
  }

  async getAccountManagers() {
    const response = await this.client.get("/accountManager");
    return response.data;
  }

  async deleteAccountManager(email: string) {
    return this.client.delete(`/accountManager?email=${email}`);
  }

  async createAdminUser(user: TUser) {
    return this.client.post("/adminUser", user);
  }

  async updateAdminUser(user: TUser) {
    return this.client.put("/adminUser", user);
  }

  async getAdminUsers() {
    const response = await this.client.get("/adminUser");
    return response.data;
  }

  async deleteAdminUser(email: string) {
    return this.client.delete(`/adminUser?email=${email}`);
  }

  async getUserRole(email: string) {
    const response = await this.client.get("/user", {
      params: { email },
    });
    return response.data;
  }

  async getWeatherForPort(unlocode: string) {
    const response = await this.client.get(`/weather/${unlocode}`);
    return response.data;
  }

  async getFeatureFlags() {
    const response = await this.client.get("/featureFlags");
    console.log("Feature flags response:", response.data);
    return response.data;
  }

  async getPortNotes() {
    const response = await this.client.get(`/portNote`);
    return response.data;
  }

  async savePortNote(portNote: TPortNote) {
    return this.client.post("/portNote", portNote);
  }

  async deletePortNote(id: string) {
    return this.client.delete(`/portNote/${id}`);
  }
}

export default ApiClient;
